import { MediaLimitsIndication } from './resolve-media-limits-indication';
import { mediaLimitsMessages } from './media-limits-messages';
import { IntlShape } from 'react-intl';

/** View-model for media-limits UX (message + whether publish must be blocked). */
export type MediaLimitsIndicationView = {
  kind: Exclude<MediaLimitsIndication['kind'], 'ok'>;
  message: string;
  /** When true, publishing must be disabled. */
  blocksPublish: boolean;
};

/**
 * Localizes a resolved media-limits indication for UI display.
 * Returns undefined when there is nothing to show (`ok`).
 *
 * @param platformName - Display name of the social platform (e.g. "YouTube"), used in soft tips.
 */
export function formatMediaLimitsIndication(
  indication: MediaLimitsIndication,
  $t: IntlShape['$t'],
  platformName: string,
): MediaLimitsIndicationView | undefined {
  switch (indication.kind) {
    case 'ok':
      return undefined;
    case 'blocked': {
      const maxMinutes =
        indication.maxDurationSec != null
          ? Math.max(1, Math.round(indication.maxDurationSec / 60))
          : undefined;
      return {
        kind: 'blocked',
        blocksPublish: true,
        message:
          maxMinutes != null
            ? $t(mediaLimitsMessages.blockedWithMax, { maxMinutes })
            : $t(mediaLimitsMessages.blocked),
      };
    }
    case 'needs_transcoding':
      return {
        kind: 'needs_transcoding',
        blocksPublish: false,
        message: $t(mediaLimitsMessages.needsTranscoding, {
          quality: indication.targetQuality,
        }),
      };
    case 'platform_displays_lower':
      return {
        kind: 'platform_displays_lower',
        blocksPublish: false,
        message: $t(mediaLimitsMessages.platformDisplaysLower, {
          platformName,
          quality: indication.maxDisplayQuality,
        }),
      };
    default:
      return undefined;
  }
}
